import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Creates an ISO string with the local timezone offset.
 * This ensures timestamps are stored correctly in timestamptz columns
 * without unintended UTC conversion.
 * 
 * Examples:
 *   toLocalISOString(new Date(2026, 1, 11, 14, 30)) => "2026-02-11T14:30:00-03:00"
 *   toLocalISOString("2026-02-11", "14:30")          => "2026-02-11T14:30:00-03:00"
 *   toLocalISOString("2026-02-11")                    => "2026-02-11T00:00:00-03:00"
 */
export function toLocalISOString(dateOrStr: Date | string, time?: string): string {
  const pad = (n: number) => String(n).padStart(2, "0");

  let d: Date;
  if (typeof dateOrStr === "string") {
    if (time) {
      const [h, m] = time.split(":").map(Number);
      d = new Date(`${dateOrStr}T${pad(h)}:${pad(m)}:00`);
    } else {
      // Parse as local date (noon to avoid DST edge)
      const [y, mo, da] = dateOrStr.split("-").map(Number);
      d = new Date(y, mo - 1, da, 0, 0, 0);
    }
  } else {
    d = dateOrStr;
  }

  const offset = -d.getTimezoneOffset();
  const sign = offset >= 0 ? "+" : "-";
  const absOff = Math.abs(offset);
  const offH = pad(Math.floor(absOff / 60));
  const offM = pad(absOff % 60);

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${sign}${offH}:${offM}`;
}
